import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RefreshCw, Plus, Pencil, GitBranch, Github, Gitlab, GitFork, Trash2, ExternalLink, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BranchSelect, BranchTag, useBranches } from "@/components/BranchBits";

const PLATFORM_META = {
  github: { icon: Github, label: "GitHub", color: "text-foreground" },
  gitlab: { icon: Gitlab, label: "GitLab", color: "text-[hsl(24_92%_55%)]" },
  bitbucket: { icon: GitFork, label: "Bitbucket", color: "text-[hsl(211_100%_60%)]" },
};

const SYNC_STATUS = {
  idle: { color: "bg-muted text-muted-foreground", label: "Idle" },
  syncing: { color: "bg-primary/15 text-primary", label: "Syncing" },
  success: { color: "bg-emerald-500/15 text-emerald-500", label: "Synced" },
  error: { color: "bg-red-500/15 text-red-500", label: "Error" },
};

export default function Repositories() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreate = isAdmin || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const [repos, setRepos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("all");
  const [open, setOpen] = useState(false);
  const EMPTY_FORM = {
    platform: "github",
    workspace: "",
    slug: "",
    project_id: "",
    default_branch: "main",
    access_token: "",
    base_url: "",
    branch_id: "",
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null); // repo being edited, or null when adding
  const [clearToken, setClearToken] = useState(false);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setClearToken(false); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setClearToken(false);
    setForm({
      platform: r.platform,
      workspace: r.workspace || "",
      slug: r.slug || "",
      project_id: r.project_id || "",
      default_branch: r.default_branch || "main",
      access_token: "", // never sent back by the API; blank = keep current
      base_url: r.base_url || "",
      branch_id: r.branch_id || "",
    });
    setOpen(true);
  };

  const load = () =>
    api.get("/git/repositories").then((r) => {
      setRepos(r.data);
      setLoading(false);
    });

  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  // Poll while any repo is syncing
  useEffect(() => {
    if (!repos.some((r) => r.sync_status === "syncing")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [repos]);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "Unlinked";

  // Consistent short date-time formatting for all timestamp columns.
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const rows = repos
    .filter((r) => filterProject === "all" || r.project_id === filterProject)
    .map((r) => ({
      ...r,
      _platform_label: PLATFORM_META[r.platform]?.label || r.platform,
      _project_name: projectName(r.project_id),
      _commits: r.stats?.commits || 0,
      _prs: r.stats?.pull_requests || 0,
      _reviews: r.stats?.reviews || 0,
      _devs: r.stats?.contributors || 0,
      _last_synced_ts: r.last_synced_at ? new Date(r.last_synced_at).getTime() : 0,
      _last_commit_ts: r.last_commit_at ? new Date(r.last_commit_at).getTime() : 0,
    }));

  const create = async () => {
    if (!form.workspace || !form.slug) return toast.error("Workspace and repo name are required");
    const hostFor = () => {
      if (form.platform === "gitlab" && form.base_url) {
        return form.base_url.trim().replace(/\/$/, "").replace(/\/api\/v4$/, "");
      }
      return `https://${form.platform === "github" ? "github.com" : form.platform === "gitlab" ? "gitlab.com" : "bitbucket.org"}`;
    };
    const url = `${hostFor()}/${form.workspace}/${form.slug}`;
    const body = {
      ...form,
      name: `${form.workspace}/${form.slug}`,
      url,
      project_id: form.project_id || null,
      access_token: form.access_token || null,
      branch_id: form.branch_id || null,
      base_url: form.platform === "gitlab" && form.base_url ? form.base_url.trim() : null,
    };
    try {
      if (editing) {
        await api.patch(`/git/repositories/${editing.id}`, { ...body, clear_token: clearToken && !form.access_token });
        toast.success("Repository updated");
      } else {
        await api.post("/git/repositories", body);
        toast.success("Repository added");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editing ? "Failed to update" : "Failed to add"));
    }
  };

  const sync = async (id) => {
    try {
      await api.post(`/git/repositories/${id}/sync`);
      toast.info("Sync started");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const syncAll = async () => {
    try {
      const { data } = await api.post("/git/sync-all");
      toast.info(`Queued sync for ${data.queued} repositories`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this repository and all its commits/PRs?")) return;
    try {
      await api.delete(`/git/repositories/${id}`);
      toast.success("Repository removed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const totals = repos.reduce(
    (acc, r) => {
      acc.commits += r.stats?.commits || 0;
      acc.prs += r.stats?.pull_requests || 0;
      acc.reviews += r.stats?.reviews || 0;
      return acc;
    },
    { commits: 0, prs: 0, reviews: 0 }
  );

  const columns = [
    {
      key: "name",
      label: "Repository",
      sortable: true,
      render: (r) => {
        const meta = PLATFORM_META[r.platform] || PLATFORM_META.github;
        const Icon = meta.icon;
        return (
          <div className="flex items-center gap-2 min-w-0">
            <div className={`h-8 w-8 rounded-lg bg-accent/60 flex items-center justify-center shrink-0 ${meta.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-medium truncate max-w-[240px]" title={r.name}>{r.name}</div>
              {r.sync_error && (
                <div className="text-[10px] text-red-500 flex items-center gap-1 truncate max-w-[240px]" title={r.sync_error}>
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  <span className="truncate">{r.sync_error}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    { key: "_platform_label", label: "Platform", sortable: true, render: (r) => <span className="capitalize text-sm">{r._platform_label}</span> },
    { key: "_project_name", label: "Project", sortable: true, render: (r) => <span className="text-sm">{r._project_name}</span> },
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    {
      key: "sync_status",
      label: "Status",
      sortable: true,
      render: (r) => {
        const status = SYNC_STATUS[r.sync_status] || SYNC_STATUS.idle;
        return (
          <Badge className={`${status.color} border-0 rounded-md text-[10px]`}>
            {r.sync_status === "syncing" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {r.sync_status === "success" && <CheckCircle2 className="w-3 h-3 mr-1" />}
            {r.sync_status === "error" && <AlertCircle className="w-3 h-3 mr-1" />}
            {status.label}
          </Badge>
        );
      },
    },
    { key: "_commits", label: "Commits", sortable: true, className: "text-right", render: (r) => <div className="text-right number">{r._commits}</div> },
    { key: "_prs", label: "PRs", sortable: true, className: "text-right", render: (r) => <div className="text-right number">{r._prs}</div> },
    { key: "_reviews", label: "Reviews", sortable: true, className: "text-right", render: (r) => <div className="text-right number">{r._reviews}</div> },
    { key: "_devs", label: "Devs", sortable: true, className: "text-right", render: (r) => <div className="text-right number">{r._devs}</div> },
    {
      key: "_last_commit_ts",
      label: "Last Commit",
      sortable: true,
      render: (r) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`last-commit-${r.id}`}>
          {fmtDateTime(r.last_commit_at)}
        </span>
      ),
    },
    {
      key: "_last_synced_ts",
      label: "Last Synced",
      sortable: true,
      render: (r) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {fmtDateTime(r.last_synced_at)}
        </span>
      ),
    },
    {
      key: "has_token",
      label: "Token",
      sortable: true,
      render: (r) => (
        <span className="text-[11px] text-muted-foreground">{r.has_token ? "🔒 Set" : "public"}</span>
      ),
    },
  ];

  const rowActions = (r) => (
    <div className="flex items-center justify-end gap-1">
      <Button
        data-testid={`sync-${r.id}`}
        size="sm"
        variant="outline"
        onClick={() => sync(r.id)}
        disabled={r.sync_status === "syncing" || !r.can_modify}
        title={!r.can_modify ? "Only the owner or Company Admin can sync" : undefined}
      >
        <RefreshCw className={`w-3 h-3 ${r.sync_status === "syncing" ? "animate-spin" : ""}`} />
      </Button>
      <Button size="sm" variant="ghost" asChild>
        <a href={r.url} target="_blank" rel="noreferrer" data-testid={`open-${r.id}`}>
          <ExternalLink className="w-3 h-3" />
        </a>
      </Button>
      {r.can_modify && (
        <Button
          data-testid={`edit-${r.id}`}
          size="sm"
          variant="ghost"
          onClick={() => openEdit(r)}
          title="Edit repository / update token"
        >
          <Pencil className="w-3 h-3" />
        </Button>
      )}
      {r.can_modify && (
        <Button
          data-testid={`delete-${r.id}`}
          size="sm"
          variant="ghost"
          onClick={() => remove(r.id)}
          className="text-red-500 hover:text-red-500"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        testid="repos-header"
        title="Repositories"
        subtitle={`${repos.length} repositories across GitHub, GitLab & Bitbucket`}
        actions={
          <div className="flex gap-2">
            {canCreate && (
              <>
                {isAdmin && <Button variant="outline" onClick={syncAll} data-testid="sync-all-btn"><RefreshCw className="w-4 h-4 mr-2" />Sync all</Button>}
                <Button data-testid="add-repo-btn" onClick={openAdd}><Plus className="w-4 h-4 mr-1" />Add Repository</Button>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogContent data-testid="repo-dialog">
                    <DialogHeader>
                      <DialogTitle>{editing ? "Edit Repository" : "Add Repository"}</DialogTitle>
                      <DialogDescription>{editing ? `Update connection details for ${editing.name}. Leave the token blank to keep the current one.` : "Connect a repo from GitHub, GitLab or Bitbucket. Provide a personal access token to sync private repos."}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>Platform</Label>
                        <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                          <SelectTrigger data-testid="repo-platform"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="github">GitHub</SelectItem>
                            <SelectItem value="gitlab">GitLab</SelectItem>
                            <SelectItem value="bitbucket">Bitbucket</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{form.platform === "gitlab" ? "Namespace / Group" : form.platform === "bitbucket" ? "Workspace" : "Owner"}</Label>
                          <Input data-testid="repo-workspace" value={form.workspace} onChange={(e) => setForm({ ...form, workspace: e.target.value })} placeholder="acme" />
                        </div>
                        <div>
                          <Label>Repository slug</Label>
                          <Input data-testid="repo-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="atlas-backend" />
                        </div>
                      </div>
                      {form.platform === "gitlab" && (
                        <div>
                          <Label>Self-hosted GitLab URL (optional)</Label>
                          <Input
                            data-testid="repo-base-url"
                            value={form.base_url}
                            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                            placeholder="http://139.59.75.200"
                          />
                          <div className="text-[11px] text-muted-foreground mt-1">
                            Leave blank to use gitlab.com. Otherwise enter the base URL of your self-hosted GitLab (e.g. <code>http://139.59.75.200</code> or <code>https://gitlab.mycorp.com</code>). The <code>/api/v4</code> suffix is added automatically.
                          </div>
                        </div>
                      )}
                      <BranchSelect value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} />
                      <div>
                        <Label>Project (optional)</Label>
                        <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                          <SelectTrigger data-testid="repo-project"><SelectValue placeholder="Link to project" /></SelectTrigger>
                          <SelectContent>
                            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Personal Access Token {editing ? "(leave blank to keep current)" : "(optional, for private repos)"}</Label>
                        <Input data-testid="repo-token" type="password" autoComplete="new-password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder={editing ? (editing.has_token ? "•••••••• (token is set)" : "No token set") : "ghp_..."} />
                        {editing?.has_token && (
                          <label className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5 cursor-pointer">
                            <input type="checkbox" data-testid="repo-clear-token" checked={clearToken} onChange={(e) => setClearToken(e.target.checked)} />
                            Remove the saved token (repo will sync as public)
                          </label>
                        )}
                        <div className="text-[11px] text-muted-foreground mt-1">
                          GitHub: <code>repo</code> scope · GitLab: <code>read_api</code> · Bitbucket: App Password with <code>repository:read</code>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                      <Button data-testid="repo-save" onClick={create}>{editing ? "Save changes" : "Add"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Repositories" value={repos.length} icon={GitBranch} tone="primary" />
        <KpiCard label="Total Commits" value={totals.commits} />
        <KpiCard label="Pull Requests" value={totals.prs} />
        <KpiCard label="Code Reviews" value={totals.reviews} />
      </div>

      <DataTable
        testid="repos-table"
        columns={columns}
        data={rows}
        loading={loading}
        empty="No repositories connected yet"
        searchKeys={["name", "_platform_label", "_project_name", "sync_status"]}
        initialPageSize={10}
        rowActions={rowActions}
        extraFilters={
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-56 h-9" data-testid="repos-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
    </div>
  );
}
